// src/components/Main.jsx
// Homepage — video hero + feature grid + sports row + Apps Coming Soon + CTA
// Mobile-first: video is portrait-safe, hero text is large and readable,
// no content is hidden behind the header (safe-area-aware)
// UPDATED: App Store / Google Play "Coming Soon" section added
// FIXED: Padel icon corrected to 🏓, Pickleball to 🥒

import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import promoVideo from "../assets/promoVideo.mp4";

const SPORTS = [
  { icon: "\u26BD",       label: "Football"   },
  { icon: "\uD83C\uDFBE", label: "Tennis"     },
  { icon: "\uD83C\uDFC0", label: "Basketball" },
  { icon: "\uD83C\uDFCF", label: "Cricket"    },
  { icon: "\uD83E\uDD52", label: "Pickleball" }, // 🥒 — correct Pickleball icon matching the backend
  { icon: "\uD83C\uDFD3", label: "Padel"      }, // 🏓 — correct Padel icon matching the backend
  // { icon: "\uD83C\uDFC3", label: "Track" }, // coming soon
  // { icon: "\uD83C\uDFCA", label: "Swimming" }, // coming soon
  // { icon: "\u26F3", label: "Golf" }, // coming soon
  { icon: "\uD83C\uDFF8", label: "Badminton"  },
  // { icon: "\uD83C\uDFCB\uFE0F", label: "CrossFit" }, // coming soon
];

const FEATURES = [
  {
    icon: "\uD83D\uDCCD",
    title: "Find games near you",
    body: "GPS-powered discovery across 7 sports. See who's playing, where, and when — then join in one tap.",
    to: "/discover",
    cta: "Discover",
  },
  {
    icon: "\uD83C\uDFC6",
    title: "Create & manage leagues",
    body: "Full round-robin and knockout brackets. Submit results, track standings, and manage your squad from one place.",
    to: "/leagues",
    cta: "Leagues",
  },
  {
    icon: "\uD83E\uDD47",
    title: "AI sports intelligence",
    body: "Tactical breakdowns and grassroots stories generated by autonomous sports agents. No filler.",
    to: "/feed",
    cta: "Read the feed",
  },
  {
    icon: "\uD83D\uDCC8",
    title: "Player standings",
    body: "See who's winning in your city, school, or academy. Filter by sport, location, and organisation.",
    to: "/standings",
    cta: "Standings",
  },
];

// ── App Store badge SVGs (inline, no external deps) ──────────────────────────
function AppleBadge() {
  return (
    <svg
      width="140"
      height="48"
      viewBox="0 0 140 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Download on the App Store — coming soon"
    >
      <rect x="0.5" y="0.5" width="139" height="47" rx="9.5" fill="#111111" stroke="#444" />
      {/* Apple logo */}
      <path
        d="M24.5 15.8c1.1-1.4 1.9-3.3 1.7-5.2-1.6.1-3.6 1.1-4.7 2.5-1 1.2-1.9 3.1-1.7 5 1.8.1 3.6-1 4.7-2.3z"
        fill="#fff"
      />
      <path
        d="M28.8 18.7c-2.6-.2-4.8 1.5-6 1.5-1.2 0-3.1-1.4-5.1-1.4-2.6.1-5 1.5-6.4 3.9-2.7 4.7-.7 11.7 1.9 15.5 1.3 1.9 2.8 4 4.8 3.9 1.9-.1 2.7-1.2 5-1.2 2.4 0 3 1.2 5.1 1.2 2.1 0 3.4-1.9 4.7-3.8 1.5-2.2 2.1-4.3 2.1-4.4-.1 0-4-.1-4-5.1 0-4.3 3.5-5.4 3.5-5.5-.2-.5-1.5-4.6-5.6-4.6z"
        fill="#fff"
      />
      {/* Text */}
      <text x="42" y="21" fill="#999" fontFamily="system-ui, sans-serif" fontSize="9" fontWeight="400">Download on the</text>
      <text x="42" y="36" fill="#fff" fontFamily="system-ui, sans-serif" fontSize="16" fontWeight="700">App Store</text>
    </svg>
  );
}



// ── App Early Access Section ───────────────────────────────────────────────
const APP_FEATURES = [
  {
    icon: "\u26BD",
    title: "Play",
    desc: "Create or join local games across 7 sports. Set skill level, team size, location and go.",
  },
  {
    icon: "\uD83C\uDFC6",
    title: "Leagues",
    desc: "Run your own league. Auto-generated fixtures, live standings, result submission.",
  },
  {
    icon: "\uD83C\uDFE0",
    title: "Rooms",
    desc: "Community spaces for every sport. Predictions, EVI scores, and live discussion.",
  },
  {
    icon: "\uD83D\uDCCA",
    title: "Standings",
    desc: "Your player profile, stats, win rate and AI-generated performance insights.",
  },
];

function AppSection() {
  var [email, setEmail]   = useState("");
  var [status, setStatus] = useState("idle"); // idle | loading | done | error

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || status === "loading" || status === "done") return;
    setStatus("loading");
    try {
      var res = await fetch("https://gametrait.com/api/request-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      setStatus(res.ok ? "done" : "error");
    } catch (e) {
      setStatus("error");
    }
  }

  return (
    <section className="border-t border-zinc-900 py-16 px-5">
      <div className="max-w-6xl mx-auto">

        {/* Feature grid */}
        <div className="mb-12 text-center">
          <p className="text-lime-400 text-xs font-mono uppercase tracking-widest mb-3">App only</p>
          <h2 className="text-white text-2xl md:text-3xl font-bold mb-2">Everything you need to play</h2>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">
            The full Gametrait experience — games, leagues, community and stats — lives in the app.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {APP_FEATURES.map(function(f) {
            return (
              <div key={f.title} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center hover:border-zinc-700 transition-colors">
                <span className="text-3xl block mb-3">{f.icon}</span>
                <p className="text-white font-bold text-sm mb-1">{f.title}</p>
                <p className="text-zinc-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Early access CTA */}
        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 px-6 py-10 sm:px-12 flex flex-col sm:flex-row items-center gap-8">
          <div className="flex-1 text-center sm:text-left">
            <p className="text-lime-400 text-xs font-mono uppercase tracking-widest mb-2">iOS — Early access</p>
            <h3 className="text-white text-xl font-bold mb-2">Gametrait on your phone</h3>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
              Join games, manage leagues, and track your standings — all from your pocket.
              Request early access and we will send you a TestFlight invite.
            </p>
          </div>

          <div className="flex flex-col gap-3 items-center sm:items-end w-full sm:w-auto">
            <a href="https://apps.apple.com/app/gametrait/id6742704573"
              className="block"
              title="Download on the App Store"
              aria-label="Download Gametrait on the App Store">
              <AppleBadge />
            </a>

            {/* Early access form */}
            {status === "done" ? (
              <p className="text-lime-400 text-xs font-mono mt-1">\u2713 Request received — we will be in touch</p>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-2 w-full sm:w-auto">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={function(e) { setEmail(e.target.value); }}
                  placeholder="your@email.com"
                  className="flex-1 sm:w-48 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-600 focus:outline-none focus:border-lime-400 transition-colors"
                />
                <button type="submit" disabled={status === "loading"}
                  className="bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black text-xs font-bold px-4 py-2 rounded-lg transition-colors whitespace-nowrap flex-shrink-0">
                  {status === "loading" ? "\u2026" : "Request access"}
                </button>
              </form>
            )}
            {status === "error" && (
              <p className="text-red-400 text-xs">Something went wrong — email us at hello@gametrait.com</p>
            )}
            <p className="text-zinc-700 text-[10px] font-mono uppercase tracking-wider">Android coming soon</p>
          </div>
        </div>

      </div>
    </section>
  );
}

export default function Main() {
  const videoRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <div className="bg-black min-h-screen">
      <SEO
        title="Gametrait | AI Sports Intelligence"
        description="Find grassroots games near you, create leagues, and follow AI-powered sports coverage across football, basketball, cricket, tennis, and more."
        url="https://gametrait.com"
      />

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ height: isMobile ? "100svh" : "100vh" }}>

        {/* Video background */}
        {!videoError && (
          <video
            ref={videoRef}
            src={promoVideo}
            autoPlay
            playsInline
            loop
            muted
            onError={() => setVideoError(true)}
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "cover", objectPosition: isMobile ? "center center" : "center top" }}
          />
        )}

        {/* Dark gradient overlay — heavier on mobile so text is always readable */}
        <div
          className="absolute inset-0"
          style={{
            background: isMobile
              ? "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.92) 100%)"
              : "linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.88) 100%)",
          }}
        />

        {/* Hero content */}
        <div
          className="relative z-10 flex flex-col justify-end h-full"
          style={{
            paddingBottom: isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 2.5rem)" : "4rem",
            paddingLeft: "max(env(safe-area-inset-left, 0px), 1.25rem)",
            paddingRight: "max(env(safe-area-inset-right, 0px), 1.25rem)",
            paddingTop: "calc(env(safe-area-inset-top, 0px) + 5rem)",
          }}
        >
          {/* Sport tags row — horizontal scroll, no wrap */}
          <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: "touch", msOverflowStyle: "none", scrollbarWidth: "none" }}>
            {SPORTS.map(function(s) {
              return (
                <span
                  key={s.label}
                  className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 bg-zinc-900/60 border border-zinc-700/50 rounded-full px-2.5 py-0.5 backdrop-blur-sm flex-shrink-0"
                >
                  {s.icon} {s.label}
                </span>
              );
            })}
          </div>

          {/* Headline */}
          <h1
            className="text-white font-bold leading-none mb-3"
            style={{ fontSize: isMobile ? "clamp(2.4rem, 10vw, 3.5rem)" : "clamp(3rem, 5vw, 4.5rem)" }}
          >
            Your game,<br />
            <span className="text-lime-400">everywhere.</span>
          </h1>

          {/* Sub */}
          <p
            className="text-zinc-300 leading-relaxed mb-7 max-w-md"
            style={{ fontSize: isMobile ? "0.95rem" : "1.1rem" }}
          >
            Find grassroots games near you. Create leagues. Follow AI-powered
            sports coverage — across 7 sports, anywhere in the world.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-3">
            <Link
              to="/discover"
              className="bg-lime-400 hover:bg-lime-300 text-black font-bold rounded-xl px-5 py-3 text-sm transition-colors"
            >
              Find a game
            </Link>
            <Link
              to="/leagues"
              className="bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl px-5 py-3 text-sm transition-colors border border-white/20 backdrop-blur-sm"
            >
              Create a league
            </Link>
          </div>
        </div>
      </section>

      {/* ── FEATURE GRID ──────────────────────────────────────────────────────── */}
      <section className="px-5 py-14 max-w-6xl mx-auto">
        <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest mb-2">Everything in one place</p>
        <h2 className="text-white text-2xl font-bold mb-8">Built for grassroots sport</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FEATURES.map(function(f) {
            return (
              <Link
                key={f.title}
                to={f.to}
                className="group bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-600 rounded-2xl p-5 transition-all flex flex-col gap-3"
              >
                <span className="text-3xl">{f.icon}</span>
                <div>
                  <h3 className="text-white font-semibold text-base mb-1">{f.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{f.body}</p>
                </div>
                <span className="text-lime-400 text-xs font-mono uppercase tracking-wider group-hover:translate-x-1 transition-transform inline-block">
                  {f.cta} {"\u2192"}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── SPORTS STRIP ──────────────────────────────────────────────────────── */}
      <section className="border-t border-zinc-900 py-10">
        <div className="max-w-6xl mx-auto px-5">
          <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest mb-6 text-center">7 sports and counting</p>
          <div className="flex flex-wrap justify-center gap-6">
            {SPORTS.map(function(s) {
              return (
                <Link
                  key={s.label}
                  to={"/discover?sport=" + s.label.toLowerCase()}
                  className="flex flex-col items-center gap-1.5 group"
                >
                  <span className="text-3xl group-hover:scale-110 transition-transform">{s.icon}</span>
                  <span className="text-zinc-500 group-hover:text-zinc-300 text-[10px] font-mono uppercase tracking-widest transition-colors">{s.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── APP EARLY ACCESS ──────────────────────────────────────────────────── */}
      <AppSection />

      {/* ── BOTTOM CTA ────────────────────────────────────────────────────────── */}
      <section className="border-t border-zinc-900 py-16 px-5 text-center">
        <h2 className="text-white text-2xl font-bold mb-3">Ready to play?</h2>
        <p className="text-zinc-400 text-sm mb-7 max-w-sm mx-auto">
          Join thousands of players organising games and leagues on Gametrait.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/discover"
            className="bg-lime-400 hover:bg-lime-300 text-black font-bold rounded-xl px-6 py-3 text-sm transition-colors"
          >
            Find a game near me
          </Link>
          <Link
            to="/feed"
            className="text-zinc-400 hover:text-white text-sm font-mono uppercase tracking-wider py-3 transition-colors"
          >
            Read the feed {"\u2192"}
          </Link>
        </div>
      </section>

    </div>
  );
}
